import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import {
  api,
  ApiError,
  type ConviteEntrevista,
  type ConviteEntrevistaResposta,
} from "../api";
import type { Row } from "../types";
import {
  IconCalendar,
  IconCheck,
  IconClock,
  IconClose,
  IconCopy,
  IconLink,
  IconUser,
} from "../icons";
import { usePodeExcluir } from "../permissoes";

/**
 * CONVITE DE ENTREVISTA — o que o candidato recebe depois que o dia foi marcado.
 *
 * O QUE ISTO RESOLVE
 * ------------------
 * A entrevista já saía marcada com dia e hora (ver AgendarEntrevistaModal), mas
 * o convite continuava sendo escrito à mão no WhatsApp, diferente a cada vez, e
 * quase sempre faltava a MESMA informação: a quem o candidato deve procurar ao
 * chegar na recepção. A pessoa aparecia no horário certo e ficava parada no
 * balcão sem saber por quem perguntar.
 *
 * Aqui o RH preenche uma vez — local, endereço, quem procurar, o que levar,
 * telefone de contato — e o sistema gera um LINK PÚBLICO (/entrevista/<token>).
 * O candidato abre e vê o dia por extenso, o horário, o endereço com o mapa,
 * quem procurar e as orientações; e responde ali mesmo se confirma presença ou
 * se não poderá vir. A resposta volta para esta tela, no bloco de
 * acompanhamento.
 *
 * DECISÕES QUE VALE EXPLICAR
 *  - O formulário nasce preenchido com o PADRÃO devolvido pelo servidor (o
 *    último local que o RH usou). Quase toda entrevista é no mesmo endereço:
 *    o caso comum tem de ser conferir e gerar, não digitar tudo de novo.
 *  - A URL completa é montada AQUI, no navegador (window.location.origin), e
 *    não no servidor: o link sai pronto no mesmo endereço por onde o RH abriu o
 *    sistema — entrou pelo portal, sai o endereço do portal; entrou pela rede
 *    interna, sai o da rede. É a mesma decisão do link da Pesquisa de Clima e
 *    do link da Triagem, e é o que evita mandar para o candidato um endereço
 *    que só abre dentro da empresa.
 *  - Nada é enviado sozinho: o botão abre a conversa do WhatsApp (ou o e-mail)
 *    com o texto pronto e o RH aperta enviar. Envio automático exigiria a API
 *    oficial da Meta — número dedicado, modelo aprovado e custo por mensagem.
 *  - Remarcar a entrevista NÃO invalida o link: quem abrir de novo já vê o
 *    horário novo. O que o sistema faz é AVISAR o RH (`mudou_depois_do_envio`)
 *    de que o candidato recebeu o convite com a data antiga e precisa ser
 *    avisado por fora — o link não corre atrás dele.
 *  - Os campos obrigatórios são o endereço e o "quem procurar". Sem eles o
 *    convite existe mas não resolve o problema que motivou a tela.
 */

/**
 * Os campos editáveis do convite — os mesmos que o servidor devolve em `padrao`.
 *
 * Declarado como texto puro de propósito: o formulário nunca guarda `null`
 * (campo vazio é ""), senão os <input> deixam de ser controlados pelo React e o
 * conteúdo digitado começa a sumir na primeira resposta do servidor.
 */
type CamposConvite = {
  local_nome: string;
  endereco: string;
  complemento: string;
  referencia: string;
  mapa_url: string;
  procurar_por: string;
  contato_nome: string;
  contato_telefone: string;
  orientacoes: string;
  documentos: string;
};

const CAMPOS_VAZIOS: CamposConvite = {
  local_nome: "",
  endereco: "",
  complemento: "",
  referencia: "",
  mapa_url: "",
  procurar_por: "",
  contato_nome: "",
  contato_telefone: "",
  orientacoes: "",
  documentos: "",
};

/** Traz para o formulário o que veio do servidor (convite gravado ou padrão). */
function camposDe(
  origem: Partial<Record<keyof CamposConvite, unknown>> | null | undefined,
): CamposConvite {
  const txt = (v: unknown) => String(v ?? "");
  return {
    local_nome: txt(origem?.local_nome),
    endereco: txt(origem?.endereco),
    complemento: txt(origem?.complemento),
    referencia: txt(origem?.referencia),
    mapa_url: txt(origem?.mapa_url),
    procurar_por: txt(origem?.procurar_por),
    contato_nome: txt(origem?.contato_nome),
    contato_telefone: txt(origem?.contato_telefone),
    orientacoes: txt(origem?.orientacoes),
    documentos: txt(origem?.documentos),
  };
}

/** "2026-09-10" → "quinta-feira, 10 de setembro de 2026". */
function porExtenso(data: string | null | undefined): string {
  const [a, m, d] = String(data ?? "").slice(0, 10).split("-").map(Number);
  if (!a || !m || !d) return "—";
  // Ano/mês/dia LOCAIS: `new Date("2026-09-10")` é lido como UTC e, em GMT-3,
  // mostra o dia anterior — o convite sairia com a data errada.
  const dt = new Date(a, m - 1, d);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleDateString("pt-BR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

/** Carimbo de data e hora (o que o servidor grava em ISO). */
function quando(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

/**
 * Copia para a área de transferência; devolve se realmente conseguiu.
 *
 * É a mesma técnica do link da Pesquisa de Clima, reescrita aqui de propósito
 * para os dois modais não ficarem amarrados um ao outro. O caminho antigo
 * (textarea + execCommand) continua existindo porque `navigator.clipboard` não
 * funciona em http sem ser localhost — que é exatamente como o RH abre o
 * sistema pela rede interna.
 */
async function copiarTexto(texto: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = texto;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      ta.remove();
      return ok;
    } catch {
      return false;
    }
  }
}

/** Conversa do WhatsApp já com a mensagem escrita (o RH só aperta enviar). */
function linkWhatsApp(telefone: string | null | undefined, texto: string): string {
  const numero = String(telefone ?? "").replace(/\D/g, "");
  // Até 11 dígitos é número brasileiro sem o código do país (DDD + número), e o
  // wa.me exige o 55 na frente; acima disso o número já veio completo. Sem
  // número, o WhatsApp abre com o texto pronto e o RH escolhe o contato.
  const base = numero
    ? `https://wa.me/${numero.length <= 11 ? `55${numero}` : numero}`
    : "https://wa.me/";
  return `${base}?text=${encodeURIComponent(texto)}`;
}

/**
 * Linha do bloco de resumo.
 *
 * Fica FORA do modal de propósito: componente declarado dentro de outro é
 * recriado a cada render do pai, o que remonta os campos e faz o cursor pular
 * para fora a cada tecla digitada (o mesmo problema já corrigido na
 * AvaliacaoPublica).
 */
function LinhaResumo({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="convite-resumo-linha">
      <span>{rotulo}</span>
      <strong>{children}</strong>
    </div>
  );
}

export default function ConviteEntrevistaModal({
  entrevista,
  onClose,
  onMudou,
}: {
  entrevista: Row;
  onClose: () => void;
  /** Avisa a tela de trás (aviso no topo da listagem). NÃO fecha o modal. */
  onMudou?: (mensagem: string) => void;
}) {
  const entrevistaId = Number(entrevista.id);
  // Revogar apaga o link gravado: mesma regra de exclusão do resto do sistema
  // (só administrador, conforme o Portal).
  const podeExcluir = usePodeExcluir();

  const [dados, setDados] = useState<ConviteEntrevistaResposta | null>(null);
  const [campos, setCampos] = useState<CamposConvite>(CAMPOS_VAZIOS);
  const [salvarPadrao, setSalvarPadrao] = useState(false);

  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [copiado, setCopiado] = useState<"link" | "mensagem" | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  /**
   * Relê o convite no servidor.
   *
   * `manterFormulario` existe por causa de um jeito real de perder trabalho:
   * o RH corrige a sala, e ANTES de salvar clica em "Marcar como enviado" (ou
   * manda pelo WhatsApp, que também carimba o envio). Essas ações precisam
   * reler os indicadores — enviado em, aberturas, "mudou depois do envio" —,
   * mas se recarregassem os CAMPOS junto, a correção que ainda estava na tela
   * seria substituída pelo que está gravado e sumiria sem aviso.
   *
   * Então: abrir a tela recarrega tudo; ação que só mexe no acompanhamento
   * recarrega só o acompanhamento.
   */
  const carregar = useCallback(
    async (manterFormulario = false) => {
      setCarregando(true);
      setErro(null);
      try {
        const r = await api.conviteEntrevista.get(entrevistaId);
        setDados(r);
        // Convite já gerado manda no que aparece na tela — é o que o candidato
        // está vendo. Sem convite, o formulário nasce com o último local usado.
        if (!manterFormulario) setCampos(camposDe(r.convite ?? r.padrao));
      } catch (e) {
        setErro(e instanceof ApiError ? e.message : "Não consegui carregar o convite.");
      } finally {
        setCarregando(false);
      }
    },
    [entrevistaId],
  );

  useEffect(() => {
    carregar();
  }, [carregar]);

  function alterar(campo: keyof CamposConvite, valor: string) {
    setCampos((atuais) => ({ ...atuais, [campo]: valor }));
    setErro(null);
  }

  const convite: ConviteEntrevista | null = dados?.convite ?? null;
  // O resumo sai do que o SERVIDOR acabou de devolver, não da linha que a
  // listagem carregou: se alguém remarcou a entrevista enquanto esta tela
  // estava aberta, o convite tem de mostrar o que está valendo agora.
  const daEntrevista = dados?.entrevista ?? null;
  const dataEntrevista = String(daEntrevista?.data ?? "").slice(0, 10);
  const semData = !dataEntrevista;
  const candidato = dados?.candidato ?? null;

  /** A URL que vai para o candidato (ver o cabeçalho: montada no navegador). */
  const url = useMemo(
    () => (convite ? `${window.location.origin}${convite.caminho}` : ""),
    [convite],
  );

  /** A mensagem pronta: o modelo do servidor com {link} já trocado pela URL. */
  const mensagem = useMemo(() => {
    const modelo = String(dados?.mensagem_modelo ?? "");
    if (!modelo) return url;
    // split/join troca TODAS as ocorrências — `replace` com texto trocaria só a
    // primeira, e o modelo pode repetir o {link} no fim da mensagem.
    const texto = modelo.split("{link}").join(url);
    // Modelo sem o marcador não pode sair sem o endereço: o link É o convite.
    return url && !texto.includes(url) ? `${texto}\n\n${url}` : texto;
  }, [dados?.mensagem_modelo, url]);

  /**
   * Grava o convite. `regerar` troca o token: o link antigo para de abrir e o
   * candidato precisa receber o novo — serve para quando o link vazou ou foi
   * repassado para outra pessoa.
   */
  async function salvar(regerar = false) {
    setErro(null);
    setAviso(null);
    if (semData) {
      setErro(
        "Esta entrevista ainda não tem dia marcado. Marque o dia antes de gerar o convite.",
      );
      return;
    }
    if (!campos.procurar_por.trim()) {
      setErro(
        "Diga quem o candidato deve procurar na recepção — é a informação que mais falta quando ele chega.",
      );
      return;
    }
    if (!campos.endereco.trim()) {
      setErro("Informe o endereço completo do local da entrevista.");
      return;
    }
    setSalvando(true);
    try {
      const r = await api.conviteEntrevista.salvar(entrevistaId, {
        local_nome: campos.local_nome.trim(),
        endereco: campos.endereco.trim(),
        complemento: campos.complemento.trim(),
        referencia: campos.referencia.trim(),
        mapa_url: campos.mapa_url.trim(),
        procurar_por: campos.procurar_por.trim(),
        contato_nome: campos.contato_nome.trim(),
        contato_telefone: campos.contato_telefone.trim(),
        orientacoes: campos.orientacoes.trim(),
        documentos: campos.documentos.trim(),
        salvar_padrao: salvarPadrao,
        regerar,
      });
      // Atualiza no lugar em vez de recarregar tudo: o servidor já devolveu o
      // convite gravado e a mensagem, e assim o que está digitado na tela
      // continua onde está.
      setDados((atuais) =>
        atuais ? { ...atuais, convite: r.convite, mensagem_modelo: r.mensagem_modelo } : atuais,
      );
      setAviso(r.mensagem);
      onMudou?.(r.mensagem);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não consegui salvar o convite.");
    } finally {
      setSalvando(false);
    }
  }

  async function gerarLinkNovo() {
    if (
      !confirm(
        "Gerar um link novo para esta entrevista?\n\n" +
          "O link enviado antes para de abrir — o candidato precisa receber o novo.",
      )
    )
      return;
    await salvar(true);
  }

  async function marcarEnviado() {
    if (!convite) return;
    setErro(null);
    try {
      const r = await api.conviteEntrevista.enviado(entrevistaId);
      setAviso(r.mensagem);
      onMudou?.(r.mensagem);
      // Recarrega porque o servidor carimba junto a data e o horário que
      // estavam valendo no envio — é esse carimbo que depois acusa a remarcação.
      // Só o acompanhamento: o que o RH digitou e ainda não salvou fica na tela.
      await carregar(true);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não consegui marcar o convite como enviado.");
    }
  }

  async function revogar() {
    if (!convite) return;
    if (
      !confirm(
        "Revogar o link do convite?\n\n" +
          "O endereço que o candidato recebeu deixa de abrir. Para convidar de novo é preciso gerar outro link.",
      )
    )
      return;
    setErro(null);
    try {
      const r = await api.conviteEntrevista.revogar(entrevistaId);
      setAviso(r.mensagem);
      onMudou?.(r.mensagem);
      await carregar();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não consegui revogar o link.");
    }
  }

  async function copiar(qual: "link" | "mensagem") {
    const ok = await copiarTexto(qual === "link" ? url : mensagem);
    if (!ok) {
      setErro("Não consegui copiar sozinho — selecione o texto e copie.");
      return;
    }
    setCopiado(qual);
    setTimeout(() => setCopiado(null), 2500);
  }

  /**
   * Mandar o link JÁ CONTA COMO ENVIO.
   *
   * Antes só o botão "Marcar como enviado" carimbava a data — e ninguém clica
   * nele depois de já ter mandado a mensagem pelo WhatsApp: o trabalho, para
   * quem está na tela, acabou ali. O resultado era um convite eternamente
   * "não enviado", e o aviso de "a entrevista mudou depois que você mandou o
   * link" — que existe justamente para o candidato não ir no dia errado —
   * nunca acendia.
   *
   * Nunca lança: o carimbo é registro interno; se ele falhar, a mensagem já
   * foi para o candidato do mesmo jeito e travar a tela não ajudaria ninguém.
   */
  async function carimbarEnvio() {
    if (!convite) return;
    try {
      await api.conviteEntrevista.enviado(entrevistaId);
      await carregar(true);
    } catch {
      /* silencioso de propósito — ver o comentário acima */
    }
  }

  function abrirWhatsApp() {
    window.open(linkWhatsApp(candidato?.telefone, mensagem), "_blank", "noopener");
    void carimbarEnvio();
  }

  function abrirEmail() {
    const assunto = `Entrevista SERCAMP${daEntrevista?.cargo ? ` — ${daEntrevista.cargo}` : ""}`;
    window.location.href =
      `mailto:${encodeURIComponent(String(candidato?.email ?? ""))}` +
      `?subject=${encodeURIComponent(assunto)}&body=${encodeURIComponent(mensagem)}`;
    void carimbarEnvio();
  }

  return createPortal(
    // Este popup pode abrir por cima de outro modal da aba Entrevistas: sem o
    // stopPropagation, o clique aqui dentro subiria até o overlay de baixo e
    // fecharia a tela inteira.
    <div
      className="modal-overlay"
      onMouseDown={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div className="modal-card convite-card" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h3>
              <IconLink size={18} /> Convite do candidato
            </h3>
            <p className="modal-sub">
              O link que o candidato recebe com o dia, o endereço e quem procurar na recepção
            </p>
          </div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body">
          {carregando && !dados ? (
            <p className="empty">Carregando o convite…</p>
          ) : (
            <>
              {/* ---------------- Para quem e para quando ---------------- */}
              <div className="convite-resumo">
                <LinhaResumo rotulo="Candidato">
                  <IconUser size={14} /> {String(daEntrevista?.candidato ?? "—")}
                </LinhaResumo>
                <LinhaResumo rotulo="Vaga / cargo">
                  {dados?.vaga
                    ? `${dados.vaga}${daEntrevista?.cargo ? ` · ${daEntrevista.cargo}` : ""}`
                    : String(daEntrevista?.cargo ?? "—")}
                </LinhaResumo>
                <LinhaResumo rotulo="Dia">
                  <IconCalendar size={14} /> {semData ? "sem dia marcado" : porExtenso(dataEntrevista)}
                </LinhaResumo>
                <LinhaResumo rotulo="Horário">
                  <IconClock size={14} />{" "}
                  {daEntrevista?.horario ? `${daEntrevista.horario}` : "a combinar"}
                </LinhaResumo>
                <LinhaResumo rotulo="Etapa">
                  {String(daEntrevista?.etapa || "—")}
                  {daEntrevista?.entrevistador ? ` · com ${daEntrevista.entrevistador}` : ""}
                </LinhaResumo>
                {(candidato?.telefone || candidato?.email) && (
                  <LinhaResumo rotulo="Contato do candidato">
                    {[candidato?.telefone, candidato?.email].filter(Boolean).join(" · ")}
                  </LinhaResumo>
                )}
              </div>

              {semData && (
                <div className="convite-aviso">
                  Esta entrevista ainda não tem dia marcado. Marque o dia (e, de preferência, o
                  horário) na linha da entrevista antes de gerar o convite: o link inteiro gira em
                  torno de quando o candidato precisa estar aqui.
                </div>
              )}

              {convite?.mudou_depois_do_envio && (
                <div className="convite-aviso">
                  <strong>A entrevista mudou depois que o convite foi enviado.</strong> O candidato
                  recebeu {porExtenso(convite.data_enviada)}
                  {convite.horario_enviado ? ` às ${convite.horario_enviado}` : ""}. O link já mostra
                  o dia e o horário novos, mas ninguém avisa a pessoa sozinho — mande a mensagem de
                  novo.
                </div>
              )}

              {erro && <div className="form-error">{erro}</div>}
              {aviso && !erro && <div className="form-ok">{aviso}</div>}

              {/* ---------------- Onde é a entrevista ---------------- */}
              <section className="convite-secao">
                <h4 className="convite-secao-titulo">Onde o candidato deve chegar</h4>
                <div className="convite-grid">
                  <label className="field">
                    <span>Local</span>
                    <input
                      value={campos.local_nome}
                      onChange={(e) => alterar("local_nome", e.target.value)}
                      placeholder="Ex.: SERCAMP Engenharia — sede"
                    />
                  </label>
                  <label className="field field-wide">
                    <span>
                      Endereço completo <span className="req">*</span>
                    </span>
                    <input
                      value={campos.endereco}
                      onChange={(e) => alterar("endereco", e.target.value)}
                      placeholder="Rua, número, bairro, cidade e estado"
                    />
                  </label>
                  <label className="field">
                    <span>Complemento / sala</span>
                    <input
                      value={campos.complemento}
                      onChange={(e) => alterar("complemento", e.target.value)}
                      placeholder="Ex.: prédio 2, recepção do térreo"
                    />
                  </label>
                  <label className="field">
                    <span>Ponto de referência</span>
                    <input
                      value={campos.referencia}
                      onChange={(e) => alterar("referencia", e.target.value)}
                      placeholder="Ex.: em frente ao posto de combustível"
                    />
                  </label>
                  <label className="field field-wide">
                    <span>Link do mapa (opcional)</span>
                    <input
                      value={campos.mapa_url}
                      onChange={(e) => alterar("mapa_url", e.target.value)}
                      placeholder="Cole aqui o endereço do Google Maps — vira um botão na tela do candidato"
                    />
                  </label>
                </div>
              </section>

              {/* ---------------- O coração do convite ---------------- */}
              <section className="convite-secao">
                <h4 className="convite-secao-titulo">Quem procurar na recepção</h4>
                <p className="modal-sub">
                  É a primeira pergunta que o candidato faz ao chegar. Escreva o nome de quem vai
                  recebê-lo — sem isso, ele fica parado no balcão.
                </p>
                <div className="convite-grid">
                  <label className="field field-wide">
                    <span>
                      Quem procurar na recepção <span className="req">*</span>
                    </span>
                    <input
                      value={campos.procurar_por}
                      onChange={(e) => alterar("procurar_por", e.target.value)}
                      placeholder="Ex.: procurar por Eliandra, do RH, na recepção"
                    />
                  </label>
                  <label className="field">
                    <span>Nome do contato</span>
                    <input
                      value={campos.contato_nome}
                      onChange={(e) => alterar("contato_nome", e.target.value)}
                      placeholder="Quem o candidato chama se atrasar ou se perder"
                    />
                  </label>
                  <label className="field">
                    <span>Telefone de contato</span>
                    <input
                      value={campos.contato_telefone}
                      onChange={(e) => alterar("contato_telefone", e.target.value)}
                      placeholder="(19) 99999-9999"
                    />
                  </label>
                </div>
              </section>

              {/* ---------------- O que levar ---------------- */}
              <section className="convite-secao">
                <h4 className="convite-secao-titulo">Orientações ao candidato</h4>
                <div className="convite-grid">
                  <label className="field field-wide">
                    <span>Orientações</span>
                    <textarea
                      rows={3}
                      value={campos.orientacoes}
                      onChange={(e) => alterar("orientacoes", e.target.value)}
                      placeholder="Ex.: chegue 10 minutos antes; há estacionamento no local; a conversa leva cerca de 40 minutos."
                    />
                  </label>
                  <label className="field field-wide">
                    <span>Documentos para levar</span>
                    <textarea
                      rows={3}
                      value={campos.documentos}
                      onChange={(e) => alterar("documentos", e.target.value)}
                      placeholder="Ex.: RG ou CNH, CPF, carteira de trabalho e currículo impresso."
                    />
                  </label>
                </div>
                <label>
                  <input
                    type="checkbox"
                    checked={salvarPadrao}
                    onChange={(e) => setSalvarPadrao(e.target.checked)}
                  />{" "}
                  Salvar este local como padrão para as próximas entrevistas
                </label>
              </section>

              {/* ---------------- O link ---------------- */}
              {convite ? (
                <section className="convite-secao">
                  <h4 className="convite-secao-titulo">O link do convite</h4>

                  <div className="convite-link">
                    <input
                      className="convite-link-url"
                      readOnly
                      value={url}
                      onFocus={(e) => e.target.select()}
                    />
                    <div className="convite-acoes">
                      <button
                        type="button"
                        className="btn-secondary btn-sm"
                        onClick={() => copiar("link")}
                      >
                        {copiado === "link" ? <IconCheck size={15} /> : <IconCopy size={15} />}{" "}
                        {copiado === "link" ? "Copiado!" : "Copiar link"}
                      </button>
                      <a
                        className="btn-secondary btn-sm"
                        // `?preview=1`: é o RH conferindo, não o candidato
                        // lendo. Sem isso, todo convite nasceria já "aberto"
                        // e o indicador de leitura não valeria nada.
                        href={`${url}?preview=1`}
                        target="_blank"
                        rel="noreferrer"
                        title="Ver a página exatamente como o candidato vai ver (não conta como leitura dele)"
                      >
                        Abrir
                      </a>
                      <button type="button" className="btn-primary btn-sm" onClick={abrirWhatsApp}>
                        Enviar pelo WhatsApp
                      </button>
                      {candidato?.email && (
                        <button
                          type="button"
                          className="btn-secondary btn-sm"
                          onClick={abrirEmail}
                          title={String(candidato.email)}
                        >
                          Enviar por e-mail
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn-secondary btn-sm"
                        onClick={marcarEnviado}
                        title="Registra a data do envio — é o que faz o sistema perceber depois se a entrevista foi remarcada"
                      >
                        Marcar como enviado
                      </button>
                      {podeExcluir && (
                        <button type="button" className="btn-secondary btn-sm" onClick={revogar}>
                          Revogar link
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="convite-preview">
                    <span className="modal-sub">Mensagem pronta — confira antes de mandar:</span>
                    <textarea
                      className="convite-preview-texto"
                      readOnly
                      rows={8}
                      value={mensagem}
                      onFocus={(e) => e.target.select()}
                    />
                    <div className="convite-acoes">
                      <button
                        type="button"
                        className="btn-secondary btn-sm"
                        onClick={() => copiar("mensagem")}
                      >
                        {copiado === "mensagem" ? <IconCheck size={15} /> : <IconCopy size={15} />}{" "}
                        {copiado === "mensagem" ? "Copiada!" : "Copiar mensagem"}
                      </button>
                    </div>
                    <p className="muted">
                      Nada é enviado sozinho: o botão abre a conversa (ou o e-mail) com este texto já
                      escrito e você aperta enviar. Depois, clique em{" "}
                      <strong>Marcar como enviado</strong> para o acompanhamento abaixo ficar certo.
                    </p>
                  </div>

                  {/* ---------------- Acompanhamento ---------------- */}
                  <div className="convite-status">
                    <div className="convite-status-item">
                      <span>Enviado</span>
                      <strong>
                        {convite.enviado_em ? quando(convite.enviado_em) : "ainda não marcado"}
                      </strong>
                    </div>
                    <div className="convite-status-item">
                      <span>Aberto pelo candidato</span>
                      <strong>
                        {convite.aberto_em
                          ? `${quando(convite.aberto_em)} · ${convite.aberturas} vez(es)`
                          : "ainda não abriu"}
                      </strong>
                    </div>
                    <div className="convite-status-item">
                      <span>Resposta</span>
                      {convite.resposta === "Confirmado" ? (
                        <span className="pill pill-ok">Confirmou presença</span>
                      ) : convite.resposta === "Nao poderei" ? (
                        <span className="pill pill-danger">Avisou que não poderá vir</span>
                      ) : (
                        <span className="pill pill-neutral">Sem resposta ainda</span>
                      )}
                    </div>
                  </div>

                  {convite.resposta && (
                    <p className="muted">
                      Respondeu em {quando(convite.respondido_em)}
                      {convite.resposta_mensagem ? ` — “${convite.resposta_mensagem}”` : ""}
                    </p>
                  )}

                  <p className="muted">
                    Link criado por {convite.criado_por || "—"} em {quando(convite.criado_em)}
                    {convite.atualizado_em ? ` · alterado em ${quando(convite.atualizado_em)}` : ""}.
                  </p>
                </section>
              ) : (
                <p className="convite-vazio">
                  O convite ainda não foi gerado. Confira os dados acima e clique em{" "}
                  <strong>Gerar link do convite</strong>: o sistema cria o endereço que você manda
                  para o candidato por WhatsApp ou e-mail.
                </p>
              )}
            </>
          )}
        </div>

        <div className="modal-foot">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={salvando}>
            Fechar
          </button>
          {convite && (
            <button
              type="button"
              className="btn-secondary"
              onClick={gerarLinkNovo}
              disabled={salvando || semData}
              title="Troca o endereço do convite: o link antigo para de abrir"
            >
              Gerar um link novo
            </button>
          )}
          <button
            type="button"
            className="btn-primary"
            onClick={() => salvar(false)}
            disabled={salvando || carregando || semData}
            title={semData ? "Marque o dia da entrevista antes de gerar o convite" : ""}
          >
            {salvando ? "Salvando…" : convite ? "Salvar alterações" : "Gerar link do convite"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
